import {
    DEFAULT_ICON_COLOR,
    PluginWorkSite,
} from 'rwl-shared/dist/models/plugin/Plugin';
import { Marker, MarkerProps } from './Marker';
import { useEffect, useMemo, useState } from 'react';
import {
    Icons,
    iconAnchor,
    iconBuilder,
    IconSymbol,
    svgStringToURL,
} from '../iconBuilder';

export type WorkSiteMarkerState =
    | 'hover'
    | 'selected'
    | 'force_selected'
    | undefined;

/**
 * Builds a standard icon. If buildIcon isn't defined, this is what is used for
 * the WorkSiteIcon.
 *
 * This is not compatible with current plugin look.
 *
 * @param workSite
 * @param state
 * @param config
 */
type StandardMarkerBuilderConfig = {
    color: string;
    iconSymbol?: IconSymbol;
    size?: number;
    includeAnchor?: boolean;
};
export const standardMarkerBuilder = (
    workSite: PluginWorkSite,
    state: WorkSiteMarkerState,
    config?: StandardMarkerBuilderConfig
): { icon: google.maps.Icon; shape?: google.maps.MarkerShape } => {
    const isShowcase = workSite.showcase;
    const iconSymbol = config?.iconSymbol;
    const size = config?.size || iconSymbol ? 40 : 10;
    const includeAnchor =
        config?.includeAnchor !== undefined ? config.includeAnchor : true;

    const isDarkPinIcon = config?.iconSymbol?.name === 'Pin Dark';

    const markers = [];
    let isReview = false;
    if (iconSymbol) {
        // Don't include the additional markers unless we are also using an icon
        isReview = !!workSite.content.find((c) => c.type === 'Review');

        const isGoogleReview = !!workSite.content.find(
            (c) => c.type === 'Review' && c.source === 'GMB'
        );

        const isFacebookReview = !!workSite.content.find(
            (c) => c.type === 'Review' && c.source === 'Facebook'
        );

        if (isGoogleReview) {
            markers.push(Icons.GOOGLE_MARKER);
            markers.push(Icons.STAR_MARKER);
        } else if (isFacebookReview) {
            markers.push(Icons.FACEBOOK_MARKER);
            markers.push(Icons.STAR_MARKER);
        } else if (isReview) {
            markers.push(Icons.STAR_MARKER);
        }
    }

    const color = config?.color ?? DEFAULT_ICON_COLOR;

    const icon = {
        url: svgStringToURL(
            iconBuilder(
                color,
                state === 'selected',
                iconSymbol,
                markers,
                size,
                isShowcase,
                isDarkPinIcon
            )
        ),
        anchor: includeAnchor ? iconAnchor(size) : undefined,
    };

    return {
        icon,
    };
};

export const WorkSiteMarker = ({
    workSite,
    state,
    onStateChange,
    style,
    ...rest
}: Omit<MarkerProps, 'position'> & {
    style?: { color: string; iconSymbol?: IconSymbol };
    workSite: PluginWorkSite;
    state?: WorkSiteMarkerState;
    onStateChange?: (
        state: WorkSiteMarkerState,
        marker: google.maps.Marker | null
    ) => void;
}): JSX.Element | null => {
    const [marker, setMarker] = useState<google.maps.Marker>();

    const iconData = useMemo(
        () => standardMarkerBuilder(workSite, state, style),
        [state]
    );

    function mouseOver() {
        onStateChange && onStateChange('hover', null);
    }

    function mouseOut() {
        onStateChange && onStateChange(undefined, null);
    }

    useEffect(() => {
        if (state === 'force_selected' && marker) {
            onStateChange && onStateChange('force_selected', marker);
        }
    }, [state, marker]);

    // can't display a marker if we do not have geolocation
    if (
        !workSite.geolocation ||
        !workSite.geolocation.lat ||
        !workSite.geolocation.lng
    ) {
        return null;
    }

    const location =
        workSite.locationInfo?.neighborhood || workSite.locationInfo?.city;

    const zIndex = state === 'selected' ? 100 : workSite.showcase ? 50 : 10;

    return (
        <Marker
            key={workSite.id}
            onMarker={(marker) => setMarker(marker)}
            position={workSite.geolocation!}
            icon={iconData.icon}
            zIndex={zIndex}
            {...rest}
            title={`View details of ${
                workSite.showcase ? 'showcase ' : ''
            }job at ${location}`}
            onMouseOver={mouseOver}
            onMouseOut={mouseOut}
        />
    );
};
